import React from 'react';
import PropTypes from 'prop-types';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  List,
  Avatar,
  Divider,
  Typography,
  Badge,
  FormControl,
  TextField,
  Autocomplete} from '@mui/material';
import {
  FileText as FileTextIcon,
} from 'react-feather';
import NavItem from './NavItem';
import { useDispatch, useSelector } from 'react-redux';
import { useCompaniesList } from '../../hooks/useCompanies';
import { useProcessorVersion } from '../../hooks/useAppResources';
import SplitToken from '../../mixins/SplitToken';
import "../Layout.css";
import { styled } from '@mui/material/styles';
import Toast from "../../components/Toast";
import { sortByString } from "../../utils/sortByString";
import { legalLinks } from '../../components/LegalLinks';
import { loginNavItems } from '../../permissions/viewRegistry';
import { canAccess } from '../../permissions/canAccess';
import {
    getGeneralNavItems,
    getMainNavItems,
    getSuperAdminNavItems,
} from '../../permissions/landingRoute';
import { AppViewIds } from '../../permissions/appViewIds';
import { useCompanyMessages } from '../../hooks/useMessages';
import { ALL_CUSTOMERS_OPTION, EMPTY_GUID } from '../../query/queryConfig';
import { usePermissionState } from '../../stores/permissionStore';

const StyledBadge = styled(Badge)(({ theme }) => ({
    '& .MuiBadge-badge': {
      right: 10,
      top: 21,
      border: `2px solid ${theme.palette.background.paper}`,
      padding: '0 4px',
    },
}));

const legalItems = legalLinks.map((item) => ({
    href: item.path,
    icon: FileTextIcon,
    title: item.label,
}));

const NavBar = ({ isMobileNavOpen }) => {
    const dispatch = useDispatch();

    const user = useSelector((state) => state.UserReducer.isAuthenticated);
    const permissionState = usePermissionState();
    const { data: processorVersion } = useProcessorVersion({ enabled: Boolean(user) });
    const currentCompany = useSelector((state) => state.CompanyReducer.userCompanies[0]);
    const userCompany = useSelector((state) => state.CompanyReducer.userCompanies);
    const { data: allCompany = [] } = useCompaniesList({ enabled: Boolean(user) });
    const userInfo = SplitToken();
    const mainNavItems = getMainNavItems(permissionState, userCompany);
    const generalNavItems = getGeneralNavItems(permissionState);
    const superAdminNavItems = getSuperAdminNavItems(permissionState);

    const customerSelect = useSelector((state) => state.UserReducer.customerSelection);

    function setSelectedCustomer(value){
        if(value.userCompanyId == EMPTY_GUID){
            dispatch({ type: 'CUSTOMER_SELECTION', payload: ALL_CUSTOMERS_OPTION });
            Toast.fire({ title: 'Customer Set', icon: 'success' });
        } else {
            dispatch({ type: 'CUSTOMER_SELECTION', payload: value });
            Toast.fire({ title: 'Customer Set', icon: 'success' });
        }
    };

    const pendingCompany = [];
    allCompany?.forEach(companyPusher);
    function companyPusher(allCompany) {
        if (allCompany.companyStatus === 0){
            pendingCompany.push({
                companyStatus: allCompany.companyStatus,
            })
        }
    };

    const showCustomerSelector = permissionState.processExternal
        && (canAccess(AppViewIds.Customers, permissionState) || canAccess(AppViewIds.CustomerEstimates, permissionState));

    const showCustomerMessageBadge = canAccess(AppViewIds.Customers, permissionState);
    const { data: allMessages = [] } = useCompanyMessages({
        companyId: currentCompany?.id,
        enabled: Boolean(user && currentCompany?.id && showCustomerMessageBadge),
    });

    let message0 = 0;
    allMessages?.forEach(messagePusher);
    function messagePusher(allMessages) {
        if (allMessages.requestStatus === 0){
            message0 += 1;
        }
    };

    let allMessagesArray = [ALL_CUSTOMERS_OPTION];
    if (Array.isArray(allMessages) && allMessages.length > 1) {
        allMessagesArray = [ALL_CUSTOMERS_OPTION, ...sortByString(allMessages, 'companyContact')];
    }
    const defaultProps = {
        options: Array.isArray(allMessagesArray) ? allMessagesArray : [ALL_CUSTOMERS_OPTION],
        getOptionLabel: (option) => option.companyContact ?? '',
        isOptionEqualToValue: (option, value) => (option?.userCompanyId && value?.userCompanyId) ? option.userCompanyId === value.userCompanyId : option === value
    };

    const content = (
        <Box
            display="flex"
            flexDirection="column"
            sx={{ border: '1px solid #e0e0e0', height: '100%', boxSizing: 'border-box' }}
            >
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    flexDirection: 'row',
                    p: 1
                }}
            >
                <Avatar
                    component={RouterLink}
                    to="/account"
                    sx={{
                        cursor: 'pointer',
                        width: 50,
                        height: 50,
                        m: 1,
                    }}
                    className='AvatarIcon'
                />
                <div>
                <Typography variant="h6" style={{marginLeft: 5}}>
                    Welcome:
                </Typography>
                <Typography variant='subtitle1'>
                    {user.userName}
                </Typography>
                </div>
            </Box>
            <div style={{marginLeft:10}}>
                <span>v. {processorVersion}</span>
            </div>
            <div>
                { showCustomerSelector ?
                    <FormControl fullWidth>
                        <Autocomplete
                            disablePortal
                            value={customerSelect}
                            {...defaultProps}
                            renderInput={(params) =>
                                <TextField
                                    {...params}
                                    label="Companies"
                                />}
                            onChange={(event,newValue) => {
                                if(newValue == null){setSelectedCustomer(ALL_CUSTOMERS_OPTION)}else{setSelectedCustomer(newValue)}}}
                         />
                    </FormControl>
                    :
                    <></>
                }
            </div>
            <Divider/>
            <Box p={1}>
                <List className="BadgeIcon">
                {mainNavItems.map((item) => (
                    <div key={item.title}>
                        { item.title == "Customer Management" ?
                            <StyledBadge badgeContent={message0} color="primary" className="BadgeIcon">
                                <NavItem
                                href={item.href}
                                title={item.title}
                                icon={item.icon}
                                />
                            </StyledBadge>
                        :
                            <NavItem
                                href={item.href}
                                title={item.title}
                                icon={item.icon}
                            />
                        }
                    </div>
                ))}
                </List>
            <Divider/>
                <List>
                    {generalNavItems.map((item) => (
                        <div key={item.title}>
                            <NavItem
                                href={item.href}
                                title={item.title}
                                icon={item.icon}
                            />
                        </div>
                    ))}
                </List>
            <Divider/>
            { superAdminNavItems.length > 0 &&
                <List>
                    {superAdminNavItems.map((item) => (
                               <div key={item.title}>
                               { item.title == "Companies" ?
                                   <StyledBadge badgeContent={pendingCompany.length} color="primary" className="BadgeIcon">
                                       <NavItem
                                       href={item.href}
                                       title={item.title}
                                       icon={item.icon}
                                       />
                                   </StyledBadge>
                               :
                                   <NavItem
                                       href={item.href}
                                       title={item.title}
                                       icon={item.icon}
                                   />
                               }
                           </div>
                    ))}
                </List>
            }
            </Box>
            <Divider/>
            <Box p={1}>
                <List>
                    {legalItems.map((item) => (
                        <NavItem
                            href={item.href}
                            key={item.title}
                            title={item.title}
                            icon={item.icon}
                        />
                    ))}
                </List>
            </Box>
            <Divider/>
            <Box flexGrow={1} />
            <Typography variant="caption" color="text.secondary" sx={{ px: 2, py: 1, fontSize: '0.7rem' }}>
                v{process.env.REACT_APP_VERSION || '0.00.0000'}
            </Typography>
        </Box>
    );

    const loginContent = (
        <Box
            display="flex"
            flexDirection="column"
            sx={{ border: '1px solid #e0e0e0', height: '100%', boxSizing: 'border-box' }}
        >
            <Box p={2}>
                <List>
                    {loginNavItems.map((item) => (
                    <NavItem
                        href={item.href}
                        key={item.title}
                        title={item.title}
                        icon={item.icon}
                    />
                    ))}
                </List>
                <Divider/>
                <List>
                    {legalItems.map((item) => (
                    <NavItem
                        href={item.href}
                        key={item.title}
                        title={item.title}
                        icon={item.icon}
                    />
                    ))}
                </List>
            </Box>
            <Box flexGrow={1} />
            <Typography variant="caption" color="text.secondary" sx={{ px: 2, py: 1, fontSize: '0.7rem' }}>
                v{process.env.REACT_APP_VERSION || '0.00.0000'}
            </Typography>
        </Box>
    )

    const sideMenuBaseSx = {
        width: 256,
        flex: '0 0 256px',
        border: 'none',
        display: 'flex',
        height: 'auto',
        alignSelf: 'stretch',
        boxSizing: 'border-box',
    };

    return <>
        {isMobileNavOpen == true ?
            <Box
                open={isMobileNavOpen}
                sx={(muiTheme) => ({
                    ...sideMenuBaseSx,
                    [muiTheme.breakpoints.down('md')]: {
                        width: '200px !important',
                        flex: '0 0 200px',
                        marginTop: '0px !important',
                        height: 'auto !important',
                    },
                })}
            >
                {user ? content : loginContent}
            </Box>
            :
            <Box
                open={isMobileNavOpen}
                sx={(muiTheme) => ({
                    ...sideMenuBaseSx,
                    [muiTheme.breakpoints.down('md')]: {
                        width: 0,
                        flex: '0 0 0',
                        border: 'none',
                        display: 'none',
                    },
                })}
            >
                {user ? content : loginContent}
            </Box>
        }
    </>;
};

NavBar.propTypes = {
    onMobileClose: PropTypes.func,
    openMobile: PropTypes.bool
};

NavBar.defaultProps = {
    onMobileClose: () => {},
    openMobile: false
};

export default NavBar;
